import React from 'react';
import { useSelector } from 'react-redux';

import { Box, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import moment from 'moment';
import { FieldArray, Form, Formik } from 'formik';

import { getBusinessDataState } from '../../../../features/store/get_business_data';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT, MISC_DATE_ISO_FORMAT, MISC_DATE_POLISH_DATE_ONLY_FORMAT, MISC_DATE_POLISH_FORMAT } from '../../../../core/constants';
import ExpirationPromotionDialog from './expiration_promotion_dialog';
import ProductPromotionDialog from './product_promotion_dialog';
import SectionPaper from "../../../common/section_paper";
import BlockIcon from "@mui/icons-material/Block";

const BusinessDataList = ({ pod, refreshList }) => {
    const getBusinessData = useSelector(getBusinessDataState);

    const initialValues = {
        businessData: getBusinessData?.data?.map(resourceData => (
            {
                ...resourceData
            }
        ))
    };

    return (
        <Formik
            initialValues={ initialValues }
            validateOnChange={ false }
            validateOnBlur={ false }
            enableReinitialize={ true }>
            { ({ values }) => (
                <Box component={ Form } sx={ { display: 'flex', flexDirection: 'column', flexGrow: 1 } }>
                    <SectionPaper
                        sx={ { p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', flexGrow: 1 } }>
                        <TableContainer sx={ { flexGrow: 1 } }>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow sx={ { '& th': { backgroundColor: 'slate.50', fontWeight: 600 } } }>
                                        <TableCell align="center">Nazwa produktu</TableCell>
                                        <TableCell align="center">Liczba zatowarowanych</TableCell>
                                        <TableCell align="center">Na stanie</TableCell>
                                        <TableCell align="center">Data zatowarowania</TableCell>
                                        <TableCell align="center">Data przydatności</TableCell>
                                        <TableCell align="center">Cena bazowa</TableCell>
                                        <TableCell align="center" colSpan={ 2 }>Cena z promocją na produkt</TableCell>
                                        <TableCell align="center" colSpan={ 2 }>Cena z promocją na krótki
                                            termin</TableCell>
                                        <TableCell align="center">Upływ terminu</TableCell>
                                        <TableCell align="center">Sprzedano</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <FieldArray name="businessData">
                                        { () => (
                                            values?.businessData?.map((resource, index) => (
                                                <TableRow key={ index } hover>
                                                    <TableCell>
                                                        <Stack direction="row" alignItems="center" spacing={1}>
                                                            <Typography
                                                                variant="body2"
                                                                sx={{
                                                                    fontWeight: 'bold',
                                                                    color: resource?.productActive === false ? 'text.secondary' : 'text.primary'
                                                                }}
                                                            >
                                                                { resource?.productName }
                                                            </Typography>

                                                            { resource?.productActive === false && (
                                                                <Tooltip title="Produkt jest nieaktywny" placement="top">
                                                                    <BlockIcon fontSize="small" color="disabled" />
                                                                </Tooltip>
                                                            )}
                                                        </Stack>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Partia: { resource?.productBatchId }
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell
                                                        align="center">{ resource?.numberOfStockedItems }</TableCell>
                                                    <TableCell
                                                        align="center">{ resource?.numberOfStockedItems - resource?.numberOfSoldItems }</TableCell>
                                                    <TableCell align="center">
                                                        {
                                                            moment(resource?.stockingDate, MISC_DATE_ISO_FORMAT)
                                                                .format(MISC_DATE_POLISH_FORMAT)
                                                        }
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        {
                                                            moment(resource?.expiryDate, MISC_DATE_ISO_DATE_ONLY_FORMAT)
                                                                .format(MISC_DATE_POLISH_DATE_ONLY_FORMAT)
                                                        }
                                                    </TableCell>
                                                    <TableCell align="right"
                                                               sx={ { backgroundColor: 'slate.100' } }>{ resource?.basicGrossPrice?.toFixed(2) }</TableCell>
                                                    <TableCell align="right"
                                                               sx={ { backgroundColor: resource?.productPromotionGrossPrice ? 'promotion.active' : resource?.containsNextOrActiveProductPromotion ? 'promotion.planned' : 'transparent' } }>
                                                        {
                                                            resource?.productPromotionGrossPrice?.toFixed(2) ||
                                                            (resource?.containsNextOrActiveProductPromotion && '%')
                                                        }
                                                    </TableCell>
                                                    <TableCell align="center"
                                                               sx={ { backgroundColor: resource?.productPromotionGrossPrice ? 'promotion.active' : resource?.containsNextOrActiveProductPromotion ? 'promotion.planned' : 'transparent' } }>
                                                        <ProductPromotionDialog resource={ resource } podId={ pod.id }
                                                                                refreshList={ refreshList }/>
                                                    </TableCell>
                                                    <TableCell align="right"
                                                               sx={ { backgroundColor: resource?.excludeExpirationPromotion ? 'promotion.excluded' : resource?.expirationPromotionGrossPrice ? 'promotion.active' : resource?.containsNextOrActiveExpirationPromotion ? 'promotion.planned' : 'transparent' } }>
                                                        {
                                                            resource?.expirationPromotionGrossPrice?.toFixed(2) ||
                                                            (resource?.containsNextOrActiveExpirationPromotion && '%')
                                                        }
                                                    </TableCell>
                                                    <TableCell align="center"
                                                               sx={ { backgroundColor: resource?.excludeExpirationPromotion ? 'promotion.excluded' : resource?.expirationPromotionGrossPrice ? 'promotion.active' : resource?.containsNextOrActiveExpirationPromotion ? 'promotion.planned' : 'transparent' } }>
                                                        <ExpirationPromotionDialog resource={ resource }
                                                                                   podId={ pod.id }
                                                                                   refreshList={ refreshList }/>
                                                    </TableCell>
                                                    <TableCell align="right">{ resource?.timePercentage }%</TableCell>
                                                    <TableCell align="right">{ resource?.salePercentage }%</TableCell>
                                                </TableRow>
                                            ))
                                        ) }
                                    </FieldArray>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionPaper>
                </Box>
            ) }
        </Formik>
    );
};

export default BusinessDataList;